import React, { useState, useEffect } from 'react';
import { RotateCcw, Volume2, VolumeX, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import BackgroundStack from './background/BackgroundStack';
import { useI18n } from '../i18n';
import LanguageSelector from './LanguageSelector';

// ─── JSON schema (lo que el backend debe devolver) ─────────────────────────────
//
// {
//   "greeting": "¡Gracias por participar en esta simulación!",   // opcional
//   "sections": [
//     {
//       "id": "identificacion",          // opcional, para debugging
//       "header": "IDENTIFICACIÓN DE LA HABILIDAD",
//       "text": "La habilidad trabajada fue **comunicación**...", // inline **bold** permitido
//       "bullets": ["**Parafraseo.** En varios momentos..."],     // opcional
//       "subitems": [                                             // opcional
//         { "title": "PARAFRASEAR EN UNA FRASE BREVE.", "text": "Esto ayuda a..." }
//       ]
//     }
//   ]
// }
// ──────────────────────────────────────────────────────────────────────────────

function extractFirstJson(text) {
    const start = text.indexOf('{');
    if (start === -1) return null;
    let depth = 0;
    let inString = false;
    let escaped = false;
    for (let i = start; i < text.length; i++) {
        const ch = text[i];
        if (escaped) { escaped = false; continue; }
        if (ch === '\\' && inString) { escaped = true; continue; }
        if (ch === '"') { inString = !inString; continue; }
        if (inString) continue;
        if (ch === '{') depth++;
        else if (ch === '}') {
            depth--;
            if (depth === 0) return text.slice(start, i + 1);
        }
    }
    return null;
}

function parseFeedbackData(raw) {
    if (!raw || !raw.trim()) return null;

    let clean = raw.trim();

    const fenceMatch = clean.match(/^```(?:json)?\s*([\s\S]*?)\s*```$/);
    if (fenceMatch) clean = fenceMatch[1].trim();

    if (clean.startsWith('"') && clean.endsWith('"')) {
        try { clean = JSON.parse(clean); } catch (_) {
            clean = clean.slice(1, -1);
        }
    }

    const tryParse = (str) => {
        try {
            const parsed = JSON.parse(str);
            if (parsed && Array.isArray(parsed.sections) && parsed.sections.length > 0) {
                return {
                    greeting: parsed.greeting || null,
                    sections: parsed.sections.map(s => ({
                        id: s.id || null,
                        header: s.header || null,
                        text: s.text || '',
                        bullets: Array.isArray(s.bullets) ? s.bullets : [],
                        subitems: Array.isArray(s.subitems)
                            ? s.subitems.map(si => ({ title: si.title || '', text: si.text || '' }))
                            : [],
                    })),
                    // scores viene del backend Arena: total + max + breakdown
                    // por dimensión (discovery / positioning / relationship).
                    // En ambiental no se manda; queda undefined.
                    scores: parsed.scores && typeof parsed.scores === 'object'
                        ? {
                            total: parsed.scores.total ?? 0,
                            max: parsed.scores.max ?? 0,
                            breakdown: parsed.scores.breakdown && typeof parsed.scores.breakdown === 'object'
                                ? parsed.scores.breakdown
                                : null,
                        }
                        : null,
                };
            }
        } catch (_) {}
        return null;
    };

    const direct = tryParse(clean);
    if (direct) return direct;

    const firstJson = extractFirstJson(clean);
    if (firstJson) {
        const fromFirst = tryParse(firstJson);
        if (fromFirst) return fromFirst;
    }

    return markdownToFeedbackData(clean);
}

function markdownToFeedbackData(text) {
    const sections = [];
    let current = null;
    let greetingLines = [];
    let foundFirstSection = false;

    for (const line of text.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const isBullet = /^[-•]\s/.test(trimmed);
        const boldMatch = trimmed.match(/^\*\*(.+?)\*\*\s*$/);
        const isMarkdownHeading = /^(##|###|#)/.test(trimmed);
        const isNumbered = /^\d+\.\s/.test(trimmed);
        const isHeader = boldMatch || isMarkdownHeading || isNumbered;

        if (isHeader) {
            foundFirstSection = true;
            const rawHeader = boldMatch
                ? boldMatch[1]
                : trimmed.replace(/^(##|###|#|\d+\.\s)/, '').trim();
            const endsWithPunctuation = /[.?!]$/.test(rawHeader);

            if (endsWithPunctuation && current) {
                current.subitems.push({ title: rawHeader, text: '' });
            } else {
                if (current) sections.push(current);
                current = { id: null, header: rawHeader, text: '', bullets: [], subitems: [] };
            }
        } else if (isBullet) {
            const bulletText = trimmed.replace(/^[-•]\s/, '').trim();
            if (current) {
                current.bullets.push(bulletText);
            } else {
                sections.push({ id: null, header: null, text: '', bullets: [bulletText], subitems: [] });
            }
        } else {
            if (!foundFirstSection) {
                greetingLines.push(trimmed);
            } else if (current && current.subitems.length > 0) {
                const last = current.subitems[current.subitems.length - 1];
                last.text += (last.text ? ' ' : '') + trimmed;
            } else if (current) {
                current.text += (current.text ? ' ' : '') + trimmed;
            } else {
                sections.push({ id: null, header: null, text: trimmed, bullets: [], subitems: [] });
            }
        }
    }
    if (current) sections.push(current);

    if (sections.length === 0) return null;

    return {
        greeting: greetingLines.length > 0 ? greetingLines.join(' ') : null,
        sections,
    };
}

// ─── Componente ───────────────────────────────────────────────────────────────

function FeedbackView({
    feedbackMessage,
    scenarioInfo,
    selectedSkills,
    skillNames,
    progress,
    onRestart,
    isTtsPlaying,
    isTtsLoading,
    onPlayTTS,
    onStopTTS,
    selectedLanguage,
    onLanguageChange,
}) {
    const { t } = useI18n();
    const [appeared, setAppeared] = useState(false);
    const [detailsOpen, setDetailsOpen] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setAppeared(true), 80);
        return () => clearTimeout(timer);
    }, []);

    const isReady = feedbackMessage && feedbackMessage.trim() !== '';
    const feedbackData = isReady ? parseFeedbackData(feedbackMessage) : null;

    // En Arena el backend calcula puntaje determinístico (3 dimensiones:
    // Descubrimiento, Posicionamiento, Vínculo, máx 500 c/u). Si llega lo
    // usamos. Si no (modo ambiental u otro), caemos a progress*1000 como
    // proxy.
    const arenaScores = feedbackData?.scores;
    const scorePoints = arenaScores?.total != null
        ? arenaScores.total
        : Math.max(0, Math.round((progress || 0) * 1000));

    // Pills compactas: primero las 3 dimensiones del scoring Arena (con
    // label y points). Si no hay scores, caemos a los headers de las 3
    // primeras secciones del feedback como antes.
    const sections = feedbackData?.sections || [];
    const breakdown = arenaScores?.breakdown;
    const compactCriteria = breakdown
        ? ['discovery', 'positioning', 'relationship']
            .filter((k) => breakdown[k])
            .map((k) => ({
                label: breakdown[k].label,
                points: breakdown[k].points,
                max: breakdown[k].max,
            }))
        : sections.slice(0, 3)
            .map((s) => ({ label: s.header, points: null, max: null }))
            .filter((c) => c.label);

    // Renderiza inline markdown: **bold** → <strong>
    const renderInline = (text) => {
        if (!text) return null;
        return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>
                : part
        );
    };

    return (
        <BackgroundStack
            className={`fixed inset-0 z-50 min-h-0 w-full overflow-y-auto transition-opacity duration-500 ${appeared ? 'opacity-100' : 'opacity-0'}`}
            sidebarExpanded={false}
        >
            {/* Top bar minimal */}
            <div className="relative z-10">
                <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center shadow-neon-cyan" style={{ background: '#22d3ee' }}>
                            <span className="text-bg-900 font-bold text-xs">S</span>
                        </div>
                        <span className="font-semibold text-white text-sm tracking-wide">SimulationAI</span>
                    </div>
                    <div className="flex items-center gap-2">
                        {selectedLanguage && onLanguageChange && (
                            <LanguageSelector selectedLanguage={selectedLanguage} onLanguageChange={onLanguageChange} />
                        )}
                    </div>
                </div>
            </div>

            <div className="relative z-10 max-w-2xl mx-auto px-6 pb-32">

                {/* Hero: eyebrow pequeño + puntaje gigante */}
                <div className="text-center mt-8 mb-6">
                    <p className="text-[10px] font-mono font-semibold tracking-[0.3em] uppercase mb-3 text-slate-400">
                        {t('sessionCompleted')}
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-6">
                        {t('yourScore')}
                    </h1>
                    {!isReady ? (
                        <div className="flex flex-col items-center gap-3 my-8">
                            <Loader2 className="w-8 h-8 text-neon-cyan animate-spin" />
                            <p className="text-sm text-slate-400">{t('generatingFeedback')}</p>
                        </div>
                    ) : (
                        <div className="flex items-baseline justify-center gap-2">
                            <span className="text-7xl sm:text-8xl font-bold tracking-tight text-white" style={{ textShadow: '0 0 32px rgba(34, 211, 238, 0.35)' }}>
                                {scorePoints}
                            </span>
                            <span className="text-2xl font-mono uppercase tracking-wider text-neon-cyan">
                                {t('pts')}
                            </span>
                        </div>
                    )}
                    {(scenarioInfo?.nombre || (skillNames && skillNames.length > 0)) && (
                        <p className="mt-4 text-xs text-slate-500 font-mono uppercase tracking-wider">
                            {scenarioInfo?.nombre}
                            {scenarioInfo?.nombre && skillNames?.length > 0 && <span className="mx-2 text-slate-700">·</span>}
                            {skillNames?.join(', ')}
                        </p>
                    )}
                </div>

                {/* 3 columnas con los componentes principales
                    (Descubrimiento / Posicionamiento / Vínculo en Arena;
                    primeras 3 secciones del feedback en otros modos).
                    Visibles por defecto; el resto del detalle queda detrás
                    del toggle "Ver más". */}
                {isReady && feedbackData && feedbackData.sections.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
                        {feedbackData.sections.slice(0, 3).map((section, i) => {
                            const dimKey = ['discovery', 'positioning', 'relationship'][i];
                            const dimScore = breakdown?.[dimKey];
                            return (
                                <div
                                    key={section.id || i}
                                    className="bg-bg-800/70 backdrop-blur-sm rounded-2xl border border-neon-cyan/15 p-4 flex flex-col"
                                >
                                    {dimScore && (
                                        <div className="flex items-baseline gap-1 mb-3">
                                            <span className="text-2xl font-bold text-neon-cyan">
                                                {dimScore.points}
                                            </span>
                                            <span className="text-xs font-mono text-slate-400">
                                                /{dimScore.max}
                                            </span>
                                        </div>
                                    )}
                                    {section.header && (
                                        <h3 className="font-semibold text-white text-xs uppercase tracking-[0.15em] mb-2">
                                            {section.header}
                                        </h3>
                                    )}
                                    {section.text && (
                                        <p className="text-xs leading-relaxed text-slate-200">
                                            {renderInline(section.text)}
                                        </p>
                                    )}
                                    {section.bullets.length > 0 && (
                                        <ul className={`space-y-1.5 ${section.text ? 'mt-2' : ''}`}>
                                            {section.bullets.map((b, j) => (
                                                <li key={j} className="flex items-start gap-1.5 text-xs text-slate-200">
                                                    <span className="w-1 h-1 rounded-full mt-1.5 shrink-0 bg-neon-cyan" />
                                                    <span className="flex-1">{renderInline(b)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Fallback: si no hay feedbackData parseado pero sí
                    compactCriteria (modo ambiental con headers como pills),
                    los seguimos mostrando como antes. */}
                {isReady && !feedbackData && compactCriteria.length > 0 && (
                    <div className="flex flex-wrap justify-center gap-2 mb-6">
                        {compactCriteria.map((c, i) => (
                            <div
                                key={i}
                                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-800/60 border border-neon-cyan/20"
                            >
                                <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan/70" />
                                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-300">
                                    {c.label}
                                </span>
                                {c.points != null && (
                                    <span className="text-[10px] font-mono font-bold text-neon-cyan">
                                        {c.points}{c.max ? `/${c.max}` : ''}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* TTS control */}
                {isReady && (isTtsLoading || isTtsPlaying || onPlayTTS) && (
                    <div className="flex justify-center mb-4">
                        {isTtsLoading ? (
                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                <span>{t('preparingAudio')}</span>
                            </div>
                        ) : isTtsPlaying ? (
                            <button
                                onClick={onStopTTS}
                                className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-full hover:bg-bg-700/50 border border-neon-cyan/20 transition-all"
                            >
                                <VolumeX className="w-3.5 h-3.5" />
                                {t('stopAudio')}
                            </button>
                        ) : onPlayTTS ? (
                            <button
                                onClick={() => onPlayTTS(feedbackMessage)}
                                className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white px-3 py-1.5 rounded-full hover:bg-bg-700/50 border border-neon-cyan/20 transition-all"
                            >
                                <Volume2 className="w-3.5 h-3.5" />
                                {t('listen')}
                            </button>
                        ) : null}
                    </div>
                )}

                {/* Toggle "Ver más" para abrir los detalles del feedback.
                    Las 3 primeras secciones ya se muestran arriba en columnas;
                    aquí desplegamos greeting, subitems y secciones extra. */}
                {(() => {
                    const extraSections = feedbackData ? feedbackData.sections.slice(3) : [];
                    const firstThreeSubitems = feedbackData
                        ? feedbackData.sections.slice(0, 3).flatMap((s, idx) =>
                            (s.subitems || []).map((sub) => ({ ...sub, parentHeader: s.header, parentIdx: idx }))
                        )
                        : [];
                    const hasHiddenContent =
                        (feedbackData?.greeting) ||
                        extraSections.length > 0 ||
                        firstThreeSubitems.length > 0 ||
                        (!feedbackData && feedbackMessage);

                    if (!isReady || !hasHiddenContent) return null;

                    return (
                        <>
                            <div className="flex justify-center mb-3">
                                <button
                                    onClick={() => setDetailsOpen((v) => !v)}
                                    className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-neon-cyan px-4 py-2 rounded-full border border-bg-700 hover:border-neon-cyan/40 transition-all"
                                >
                                    {detailsOpen ? t('showLess') : t('showMore')}
                                    {detailsOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                </button>
                            </div>

                            {detailsOpen && (
                                <div className="space-y-3 animate-fade-in">
                                    {feedbackData ? (
                                        <>
                                            {feedbackData.greeting && (
                                                <p className="text-sm text-slate-300 leading-relaxed text-center mb-4 max-w-2xl mx-auto">
                                                    {renderInline(feedbackData.greeting)}
                                                </p>
                                            )}

                                            {firstThreeSubitems.length > 0 && (
                                                <div className="bg-bg-800/70 backdrop-blur-sm rounded-2xl border border-neon-cyan/15 p-5 sm:p-6 space-y-4">
                                                    {firstThreeSubitems.map((sub, j) => (
                                                        <div key={j} className="border-l-2 border-neon-cyan/40 pl-3">
                                                            {sub.parentHeader && (
                                                                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">
                                                                    {sub.parentHeader}
                                                                </p>
                                                            )}
                                                            <p className="text-xs font-semibold text-white mb-1">{sub.title}</p>
                                                            {sub.text && (
                                                                <p className="text-xs text-slate-300 leading-relaxed">
                                                                    {renderInline(sub.text)}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {extraSections.map((section, i) => (
                                                <div
                                                    key={section.id || `extra-${i}`}
                                                    className="bg-bg-800/70 backdrop-blur-sm rounded-2xl border border-neon-cyan/15 p-5 sm:p-6 flex gap-4"
                                                >
                                                    <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border border-neon-cyan/30 text-neon-cyan bg-bg-900/60">
                                                        {i + 4}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        {section.header && (
                                                            <h3 className="font-semibold text-white text-xs uppercase tracking-[0.15em] mb-2.5">
                                                                {section.header}
                                                            </h3>
                                                        )}

                                                        {section.text && (
                                                            <p className="text-sm leading-relaxed text-slate-200">
                                                                {renderInline(section.text)}
                                                            </p>
                                                        )}

                                                        {section.bullets.length > 0 && (
                                                            <ul className={`space-y-2 ${section.text ? 'mt-3' : ''}`}>
                                                                {section.bullets.map((b, j) => (
                                                                    <li key={j} className="flex items-start gap-2 text-sm text-slate-200">
                                                                        <span className="w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ background: '#22d3ee' }} />
                                                                        <span className="flex-1">{renderInline(b)}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}

                                                        {section.subitems.length > 0 && (
                                                            <div className={`space-y-3 ${section.text || section.bullets.length > 0 ? 'mt-4' : ''}`}>
                                                                {section.subitems.map((sub, j) => (
                                                                    <div key={j} className="border-l-2 border-neon-cyan/40 pl-3">
                                                                        <p className="text-xs font-semibold text-white mb-1">{sub.title}</p>
                                                                        {sub.text && (
                                                                            <p className="text-xs text-slate-300 leading-relaxed">
                                                                                {renderInline(sub.text)}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="bg-bg-800/70 backdrop-blur-sm rounded-2xl border border-neon-cyan/15 p-6">
                                            <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap">
                                                {renderInline(feedbackMessage)}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    );
                })()}
            </div>

            {/* Bottom CTA */}
            <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-bg-900 via-bg-900/90 to-transparent pt-8 pb-6 z-20">
                <div className="max-w-3xl mx-auto px-6 flex items-center justify-center gap-3">
                    <button
                        onClick={onRestart}
                        className="flex items-center gap-2 px-7 py-3 rounded-full text-sm font-semibold text-bg-900 transition-all hover:scale-[1.03] shadow-neon-cyan"
                        style={{ background: '#22d3ee' }}
                    >
                        <RotateCcw className="w-4 h-4" />
                        {t('newSimulation')}
                    </button>
                </div>
            </div>
        </BackgroundStack>
    );
}

export default FeedbackView;
